import { z } from 'zod';
/**
 * Personal information schema
 */
export const PersonalInfoSchema = z.object({
    name: z.string().nullable(),
    email: z.string().email().nullable().or(z.null()),
    phone: z.string().nullable(),
    location: z.string().nullable(),
});
/**
 * Education entry schema
 */
export const EducationSchema = z.object({
    institution: z.string(),
    degree: z.string(),
    year: z.string().nullable(),
    field: z.string().nullable(),
});
/**
 * Work experience entry schema
 */
export const ExperienceSchema = z.object({
    company: z.string(),
    role: z.string(),
    duration: z.string(),
    description: z.array(z.string()),
});
/**
 * Certification entry schema
 */
export const CertificationSchema = z.object({
    name: z.string(),
    issuer: z.string().nullable(),
    year: z.string().nullable(),
});
/**
 * Project entry schema
 */
export const ProjectSchema = z.object({
    name: z.string(),
    description: z.array(z.string()),
    url: z.string().nullable(),
});
/**
 * Language entry schema
 */
export const LanguageSchema = z.object({
    name: z.string(),
    proficiency: z.string().nullable(),
});
/**
 * Link entry schema (LinkedIn, GitHub, portfolio, ...)
 */
export const ResumeLinkEntrySchema = z.object({
    label: z.string(),
    url: z.string(),
});
/**
 * Complete resume data schema
 */
export const ResumeDataSchema = z.object({
    personal: PersonalInfoSchema,
    summary: z.string().nullable(),
    skills: z.array(z.string()),
    education: z.array(EducationSchema),
    experience: z.array(ExperienceSchema),
    // Added after the initial extraction schema shipped — optional (defaulted
    // to []) so older callers/fixtures that predate these fields still
    // validate without changes.
    certifications: z.array(CertificationSchema).default([]),
    projects: z.array(ProjectSchema).default([]),
    languages: z.array(LanguageSchema).default([]),
    links: z.array(ResumeLinkEntrySchema).default([]),
});
/**
 * AI-generated cover letter draft — three body paragraphs the caller slots
 * into the existing CoverLetterContent shape alongside the recipient/company
 * fields the user already provided.
 */
export const CoverLetterDraftSchema = z.object({
    introduction: z.string(),
    body: z.string(),
    closing: z.string(),
});
/**
 * Parsed resume response schema (with metadata)
 */
export const ParsedResumeSchema = z.object({
    success: z.boolean(),
    data: ResumeDataSchema,
    /** 0-1 completeness/reliability score. Low values (heuristic fallback parses,
     * used when the LLM call fails) mean the caller should prompt the user to
     * double-check the imported fields rather than trust them outright. */
    confidence: z.number(),
    metadata: z.object({
        fileName: z.string(),
        fileType: z.string(),
        fileSize: z.number(),
        parsedAt: z.string(),
    }),
});
// ==========================================
// Validation Helpers
// ==========================================
/**
 * Validate resume data
 * @param data - Data to validate
 * @returns Validated resume data
 * @throws ZodError if validation fails
 */
export function validateResumeData(data) {
    return ResumeDataSchema.parse(data);
}
/**
 * Safely validate resume data
 * @param data - Data to validate
 * @returns Result object with success status and data/error
 */
export function safeValidateResumeData(data) {
    const result = ResumeDataSchema.safeParse(data);
    if (result.success) {
        return { success: true, data: result.data };
    }
    return { success: false, error: result.error };
}
/** Validate an AI-generated cover letter draft */
export function validateCoverLetterDraft(data) {
    return CoverLetterDraftSchema.parse(data);
}
/**
 * Validate parsed resume response
 * @param data - Data to validate
 * @returns Validated parsed resume response
 * @throws ZodError if validation fails
 */
export function validateParsedResume(data) {
    return ParsedResumeSchema.parse(data);
}
